import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Sparkle } from "@phosphor-icons/react";
import { useSeo } from "../lib/useSeo";
import { useLang } from "../lib/i18n";
import { COMPANY } from "../lib/constants";
import { Reveal } from "../components/blocks";
import { PlanCards, CycleSwitch } from "../components/PlanCards";
import {
  CREDITS, ADDONS, PATIENT_PAYMENTS, COMING_NEXT, FOUNDING, inr, IPD_USAGE_RATE, STARTING_PRICE, SETUP_RANGE,
} from "../config/pricing";

const STR = {
  en: {
    eyebrow: "Pricing",
    h1: "Simple pricing for every kind of facility.",
    sub: "Plans from ₹1,799 a month. No bed-based bands, unlimited staff users on every plan, and no AMC.",
    comingNext: COMING_NEXT.en,
    creditsTitle: "Credits — pay only for what has a real per-use cost",
    creditsIntro: "1 credit = ₹1. Plan credits are added every month; extra credits are bought as prepaid recharges with a GST invoice each time.",
    creditsHead: ["Usage", "Credits"],
    creditsNote1: "Included AI reads: 4 per IPD patient per day, 2 per Day Care session.",
    creditsNote2: "Clinical work and data export never stop, whatever the balance. Unused purchased credits are refundable on exit, minus GST.",
    addonsTitle: "Add-ons",
    patientsTitle: "For your patients",
    patientsSub: "Online payments on the hospital's own website.",
    foundingTitle: "Founding Partner offer",
    foundingSub: `${FOUNDING.seats} seats only, offer valid until ${FOUNDING.validUntil.en}.`,
    foundingPerks: FOUNDING.perks.en,
    foundingReturn: FOUNDING.inReturn.en,
    foundingCta: "Apply as a founding partner",
    multiTitle: "Running more than one facility?",
    multiSub: "Talk to us for a custom quote.",
    multiCta: "Talk to us",
    finePrint: [
      "All prices in Indian Rupees, exclusive of 18% GST",
      "GST invoice for every payment",
      "Cancel any time — see Refund & Cancellation Policy",
      "Pay by UPI, card, net banking or bank transfer",
    ],
    refundLink: "Refund & Cancellation Policy",
    faqTitle: "Pricing questions",
    includedTitle: "In every plan",
    included: [
      "Unlimited staff users — no per-user charges",
      "No AMC and no lock-in, cancel any time",
      "Training in Hindi and founder-led support",
      "GST invoice for every payment, data stored in India",
    ],
    faqs: [
      { q: "How much does Atikxzio HMS cost?", a: `Plans start at ${STARTING_PRICE} a month for clinics and ₹4,999 a month for hospitals, plus ${inr(IPD_USAGE_RATE)} per IPD patient-day or Day Care session, all + GST. Pay annually and get 2 months free with the setup fee waived. No AMC, no per-user charges.` },
      { q: "Is there a setup fee?", a: `A one-time setup fee covers configuration, data migration and training (${inr(SETUP_RANGE.min)} to ${inr(SETUP_RANGE.max)} by plan). It is waived when you pay annually.` },
      { q: "How do we pay?", a: "Monthly or annual plan, plus prepaid credits for WhatsApp/SMS and extra AI reads. GST invoice every time." },
    ],
  },
  hi: {
    eyebrow: "प्राइसिंग",
    h1: "हर तरह की सुविधा के लिए आसान प्राइसिंग।",
    sub: "प्लान ₹1,799 प्रति माह से। बेड के हिसाब से कोई बैंड नहीं, हर प्लान में अनलिमिटेड स्टाफ़ यूज़र, और कोई AMC नहीं।",
    comingNext: COMING_NEXT.hi,
    creditsTitle: "क्रेडिट — सिर्फ़ उसी का भुगतान जिसकी असली प्रति-उपयोग लागत है",
    creditsIntro: "1 क्रेडिट = ₹1। प्लान के क्रेडिट हर महीने जुड़ते हैं; अतिरिक्त क्रेडिट प्रीपेड रिचार्ज के रूप में ख़रीदे जाते हैं, हर बार GST इनवॉइस के साथ।",
    creditsHead: ["उपयोग", "क्रेडिट"],
    creditsNote1: "शामिल AI रीड: प्रति IPD मरीज़ प्रतिदिन 4, प्रति डे केयर सेशन 2।",
    creditsNote2: "बैलेंस चाहे जो हो, क्लिनिकल काम और डेटा एक्सपोर्ट कभी नहीं रुकते। बिना उपयोग किए ख़रीदे गए क्रेडिट बाहर निकलते समय वापस होते हैं, GST घटाकर।",
    addonsTitle: "ऐड-ऑन",
    patientsTitle: "आपके मरीज़ों के लिए",
    patientsSub: "अस्पताल की अपनी वेबसाइट पर ऑनलाइन भुगतान।",
    foundingTitle: "फ़ाउंडिंग पार्टनर ऑफ़र",
    foundingSub: `सिर्फ़ ${FOUNDING.seats} सीटें, ऑफ़र ${FOUNDING.validUntil.hi} तक मान्य।`,
    foundingPerks: FOUNDING.perks.hi,
    foundingReturn: FOUNDING.inReturn.hi,
    foundingCta: "फ़ाउंडिंग पार्टनर के लिए आवेदन करें",
    multiTitle: "एक से ज़्यादा सुविधाएँ चला रहे हैं?",
    multiSub: "कस्टम कोटेशन के लिए हमसे बात करें।",
    multiCta: "हमसे बात करें",
    finePrint: [
      "सभी क़ीमतें भारतीय रुपये में, 18% GST अतिरिक्त",
      "हर भुगतान पर GST इनवॉइस",
      "कभी भी रद्द करें — रिफ़ंड व कैंसलेशन नीति देखें",
      "UPI, कार्ड, नेट बैंकिंग या बैंक ट्रांसफ़र से भुगतान",
    ],
    refundLink: "रिफ़ंड व कैंसलेशन नीति",
    faqTitle: "प्राइसिंग से जुड़े सवाल",
    includedTitle: "हर प्लान में शामिल",
    included: [
      "अनलिमिटेड स्टाफ़ यूज़र — कोई प्रति-यूज़र शुल्क नहीं",
      "न AMC, न लॉक-इन; कभी भी रद्द करें",
      "हिंदी में ट्रेनिंग और फ़ाउंडर-नेतृत्व वाला सपोर्ट",
      "हर भुगतान पर GST इनवॉइस, डेटा भारत में संग्रहित",
    ],
    faqs: [
      { q: "Atikxzio HMS की क़ीमत कितनी है?", a: `क्लिनिक के लिए प्लान ${STARTING_PRICE} प्रति माह और अस्पतालों के लिए ₹4,999 प्रति माह से शुरू, साथ में प्रति IPD मरीज़-दिन या डे केयर सेशन ${inr(IPD_USAGE_RATE)}, सभी पर GST अतिरिक्त। सालाना भुगतान पर 2 महीने मुफ़्त और सेटअप शुल्क माफ़।` },
      { q: "क्या सेटअप शुल्क लगता है?", a: `एक बार का सेटअप शुल्क कॉन्फ़िगरेशन, डेटा माइग्रेशन और ट्रेनिंग के लिए होता है (प्लान के अनुसार ${inr(SETUP_RANGE.min)} से ${inr(SETUP_RANGE.max)})। सालाना भुगतान पर यह माफ़ है।` },
      { q: "भुगतान कैसे होता है?", a: "मासिक या सालाना प्लान, साथ में WhatsApp/SMS और अतिरिक्त AI रीड के लिए प्रीपेड क्रेडिट। हर बार GST इनवॉइस।" },
    ],
  },
};

export default function Pricing() {
  const { lang } = useLang();
  const s = STR[lang];
  const [cycle, setCycle] = useState("monthly");

  useSeo({
    title: "Atikxzio HMS Pricing | Plans from ₹1,799/month — Priyajit Systems",
    description: "Transparent Atikxzio HMS pricing: Clinic ₹1,799, Essential ₹4,999, Professional ₹5,999 and Enterprise from ₹19,999 a month, plus ₹20 per IPD patient-day. Annual payment gets 2 months free with the setup fee waived.",
    keywords: "hospital management software pricing India, HMS price, Atikxzio HMS pricing, clinic software price, hospital software cost",
    image: "/og-image.jpg",
  });

  return (
    <div className="bg-white">
      {/* Dark page header */}
      <section className="relative overflow-hidden bg-navy pt-36 lg:pt-44" data-testid="pricing-hero">
        <div aria-hidden className="absolute inset-0 bg-dots opacity-50" />
        <div aria-hidden className="absolute -right-24 -top-20 h-[26rem] w-[26rem] rounded-full bg-electric/25 blur-[130px]" />
        <div className="relative mx-auto max-w-[1200px] px-5 pb-16 lg:px-8 lg:pb-20">
          <div className="grid items-end gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-electric-light">{s.eyebrow}</p>
              <h1 className="mt-4 max-w-3xl font-display text-[2.25rem] font-black leading-[1.12] tracking-tight text-white sm:text-5xl lg:text-[3.5rem]">{s.h1}</h1>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300">{s.sub}</p>
              <div className="mt-8"><CycleSwitch cycle={cycle} setCycle={setCycle} lang={lang} /></div>
            </Reveal>
            <Reveal delay={0.12} className="lg:col-span-5">
              <div className="rounded-2xl border border-white/15 bg-white/[0.06] p-6 backdrop-blur-sm" data-testid="pricing-included">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/60">{s.includedTitle}</p>
                <ul className="mt-4 space-y-3">
                  {s.included.map((i) => (
                    <li key={i} className="flex items-start gap-3 text-sm font-semibold text-slate-200">
                      <CheckCircle size={17} weight="fill" className="mt-0.5 shrink-0 text-teal" /> {i}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="bg-mist py-16 lg:py-20" data-testid="pricing-plans">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-8">
          <h2 className="sr-only">{lang === "hi" ? "प्लान" : "Plans"}</h2>
          <PlanCards cycle={cycle} lang={lang} />
          <p className="mt-6 text-sm font-semibold text-ink" data-testid="pricing-coming-next">{s.comingNext}</p>
        </div>
      </section>

      {/* Credits */}
      <section className="bg-white py-20 lg:py-24" data-testid="pricing-credits">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-8">
          <Reveal>
            <h2 className="max-w-2xl font-display text-2xl font-bold text-navy md:text-[1.75rem]">{s.creditsTitle}</h2>
            <p className="mt-3 max-w-2xl text-base text-slate-600">{s.creditsIntro}</p>
          </Reveal>
          <div className="mt-8 overflow-hidden rounded-2xl border border-navy/10 bg-white">
            <table className="w-full border-collapse text-left text-[15px]">
              <thead>
                <tr className="bg-slate-50">
                  {s.creditsHead.map((h) => (
                    <th key={h} className="border-b border-navy/10 px-5 py-3.5 font-display text-sm font-bold text-navy">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {CREDITS.map((c, i) => (
                  <tr key={i}>
                    <th scope="row" className="border-b border-navy/[0.06] px-5 py-3.5 text-left font-normal text-slate-700">{c[lang]}</th>
                    <td className="border-b border-navy/[0.06] px-5 py-3.5 font-display font-bold text-navy">{c.credits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-5 text-sm text-slate-600">{s.creditsNote1}</p>
          <p className="mt-2 text-sm text-slate-600">{s.creditsNote2}</p>

          {/* Add-ons */}
          <h2 className="mt-16 font-display text-2xl font-bold text-navy md:text-[1.75rem]">{s.addonsTitle}</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-3" data-testid="pricing-addons">
            {ADDONS.map((a) => (
              <div key={a.name} className="rounded-2xl border border-navy/10 bg-mist p-6">
                <p className="font-display text-base font-bold text-navy">{a.name}</p>
                <p className="mt-2 font-display text-2xl font-black text-navy">{inr(a.price)}<span className="text-sm font-semibold text-slate-500">/month</span></p>
                <p className="mt-1.5 text-xs font-bold uppercase tracking-wider text-teal-ink">{a.status[lang]}</p>
              </div>
            ))}
          </div>

          {/* For your patients */}
          <div className="mt-16 rounded-2xl border border-navy/10 bg-mist p-7 lg:p-9" data-testid="pricing-patient-payments">
            <h2 className="font-display text-2xl font-bold text-navy md:text-[1.75rem]">{s.patientsTitle}</h2>
            <p className="mt-2 text-base text-slate-600">{s.patientsSub}</p>
            <ul className="mt-5 space-y-3">
              {PATIENT_PAYMENTS.map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-[15px] leading-relaxed text-slate-700">
                  <CheckCircle size={18} weight="fill" className="mt-0.5 shrink-0 text-teal" /> {p[lang]}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Founding partner */}
      <section id="founding" className="bg-navy-deep py-20 lg:py-24" data-testid="pricing-founding">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white">
            <Sparkle size={14} weight="fill" /> {s.foundingSub}
          </span>
          <h2 className="mt-5 max-w-2xl font-display text-3xl font-black tracking-tight text-white sm:text-4xl">{s.foundingTitle}</h2>
          <ul className="mt-7 grid gap-3 sm:grid-cols-2">
            {s.foundingPerks.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm font-semibold text-slate-200">
                <CheckCircle size={18} weight="fill" className="mt-0.5 shrink-0 text-teal" /> {p}
              </li>
            ))}
          </ul>
          <p className="mt-6 max-w-2xl text-sm text-slate-400">{s.foundingReturn}</p>
          <Link
            to="/contact?interest=founding"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-electric-ink px-7 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-[#0F4FA3]"
            data-testid="pricing-founding-cta"
          >
            {s.foundingCta} <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      {/* Pricing FAQ */}
      <section className="bg-white py-20" data-testid="pricing-faq">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-navy md:text-[1.75rem]">{s.faqTitle}</h2>
          <div className="mt-7 grid gap-5 lg:grid-cols-3">
            {s.faqs.map((f, i) => (
              <div key={f.q} className="rounded-2xl border border-navy/10 bg-mist p-6" data-testid={`pricing-faq-${i}`}>
                <p className="font-display text-sm font-bold text-navy">{f.q}</p>
                <p className="mt-2.5 text-sm leading-relaxed text-ink">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Multi-facility + fine print */}
      <section className="bg-mist py-20" data-testid="pricing-fineprint">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-8">
          <div className="flex flex-col items-start justify-between gap-5 rounded-2xl border border-navy/10 bg-navy/[0.03] p-7 sm:flex-row sm:items-center">
            <div>
              <p className="font-display text-lg font-bold text-navy">{s.multiTitle}</p>
              <p className="mt-1 text-sm text-slate-600">{s.multiSub}</p>
            </div>
            <Link to="/contact" className="rounded-full border border-navy/15 bg-white px-6 py-3 text-sm font-bold text-navy transition-colors duration-200 hover:border-electric-ink hover:text-electric-ink" data-testid="pricing-custom-quote-cta">
              {s.multiCta}
            </Link>
          </div>

          <ul className="mt-8 flex flex-wrap gap-x-8 gap-y-2.5 text-sm text-slate-600">
            {s.finePrint.map((f, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-electric" />
                {i === 2 ? (
                  <span>
                    {lang === "hi" ? "कभी भी रद्द करें — " : "Cancel any time — see "}
                    <Link to="/refund-policy" className="font-semibold text-electric-ink underline underline-offset-2">{s.refundLink}</Link>
                  </span>
                ) : f}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs text-slate-500">
            {COMPANY.legal} · CIN {COMPANY.cin} · GSTIN {COMPANY.gstin} · {inr(IPD_USAGE_RATE)} per IPD patient-day applies to Essential, Professional and Enterprise. Plans start at {STARTING_PRICE}/month.
          </p>
        </div>
      </section>
    </div>
  );
}
